/**
 * /admin — the unified administration console (polish task 04). One surface
 * with three sections: People (users), Groups, and Access (the advanced
 * raw-assignment / break-glass tools). Replaces the split between
 * Settings→Users and the standalone /admin/access page.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { NavLink, Outlet } from 'react-router-dom';
import { useTenantAreas } from '../../lib/access/useTenantAreas';
import { useAccessRequests } from './Access/hooks';
const tabClass = ({ isActive })=>`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${isActive ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`;
export function AdminLayout() {
    // Same query key as ActivityPage's useAccessRequests('pending') — one
    // cache entry serves both, no extra request. A non-reviewer only gets
    // their own filings back (not "awaiting their review"), so the count is
    // reviewer-gated. useDecideAccessRequest invalidates this key on
    // approve/deny, which is what keeps the number live — do not add polling.
    const { data: pendingRequests } = useAccessRequests('pending');
    const pendingCount = pendingRequests?.requests.length ?? 0;
    const showPendingCount = pendingRequests?.reviewer && pendingCount > 0;
    // Non-admins legitimately reach this layout via /admin/access (the
    // self-service panel is deliberately ungated), but /admin/activity sits
    // behind RequireAdminRoute and silently bounces them to the homepage —
    // the SAD-208 QA report. Send them to /requests instead: the
    // reviewer-flag-gated twin of the same queue, which shows a polite
    // reviewers-only note to everyone else — and the only nav affordance a
    // non-admin grantor has to their queue at all.
    //
    // SAD-948: tabs a viewer cannot use are absent, not a redirect on click.
    // People, Groups, Permissions and Access (advanced) are access
    // administration — admin/internal only, never grantable. Usage and
    // Notifications are company settings — admin/internal or the Company
    // Settings role. isAdmin mirrors RequireAdminRoute's roster.
    const areas = useTenantAreas();
    const isAdmin = areas.isAdmin || areas.isInternal;
    const canCompanySettings = areas.canManageCompanySettings;
    return /*#__PURE__*/ _jsxs("div", {
        className: "container mx-auto space-y-6 p-6",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("h1", {
                        className: "text-2xl font-semibold",
                        children: "Administration"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "Manage people, groups, and who can access what."
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("nav", {
                className: "flex gap-1 border-b",
                "data-testid": Testids.adminConsole.nav,
                children: [
                    isAdmin && /*#__PURE__*/ _jsx(NavLink, {
                        to: "/admin/people",
                        className: tabClass,
                        title: "Everyone with a login — add people, then open a person to see and change what they can reach",
                        "data-testid": Testids.adminConsole.peopleTab,
                        children: "People"
                    }),
                    isAdmin && /*#__PURE__*/ _jsx(NavLink, {
                        to: "/admin/groups",
                        className: tabClass,
                        title: "Put people in a team once and grant the team access — members inherit it automatically",
                        "data-testid": Testids.adminConsole.groupsTab,
                        children: "Groups"
                    }),
                    isAdmin && /*#__PURE__*/ _jsx(NavLink, {
                        to: "/admin/permissions",
                        className: tabClass,
                        title: "What each role means — read down a column to see everything a role can see and do",
                        "data-testid": Testids.adminConsole.permissionsTab,
                        children: "Permissions"
                    }),
                    isAdmin && /*#__PURE__*/ _jsx(NavLink, {
                        to: "/admin/access-rules",
                        className: tabClass,
                        title: "Who can see or do each sensitive thing — change a rule, or reset it to the default",
                        "data-testid": Testids.adminConsole.accessRulesTab,
                        children: "Access rules"
                    }),
                    /*#__PURE__*/ _jsxs(NavLink, {
                        to: isAdmin ? '/admin/activity' : '/requests',
                        className: tabClass,
                        title: "Access requests waiting on a decision, plus the log of every access change",
                        "data-testid": Testids.adminConsole.activityTab,
                        children: [
                            "Requests & history",
                            showPendingCount && /*#__PURE__*/ _jsxs("span", {
                                "data-testid": Testids.adminConsole.activityTabCount,
                                children: [
                                    " (",
                                    pendingCount,
                                    ")"
                                ]
                            })
                        ]
                    }),
                    canCompanySettings && /*#__PURE__*/ _jsx(NavLink, {
                        to: "/admin/notifications",
                        className: tabClass,
                        title: "Delivery status for recent notifications — per recipient and per channel (email/Teams)",
                        "data-testid": Testids.adminNotificationDelivery.tab,
                        children: "Notifications"
                    }),
                    isAdmin && /*#__PURE__*/ _jsx(NavLink, {
                        to: "/admin/access",
                        className: tabClass,
                        title: "Power tools: look up any user's grants, browse the org tree, emergency access",
                        "data-testid": Testids.adminConsole.accessTab,
                        children: "Access (advanced)"
                    }),
                    canCompanySettings && /*#__PURE__*/ _jsx(NavLink, {
                        to: "/admin/usage",
                        className: tabClass,
                        title: "Which AI model chats run on, and token usage per person",
                        "data-testid": Testids.adminConsole.usageTab,
                        children: "Model & usage"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Outlet, {})
        ]
    });
}
